import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ChevronLeft, ChevronRight, Loader2, RefreshCw, ExternalLink, Search, X,
  CalendarDays, Users, Euro, Receipt, MapPin, Plus, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  syncHelloAssoForms, fetchHelloAssoForms, fetchHelloAssoForm,
  createManualEvent, updateManualEvent, deleteManualEvent,
  type HelloAssoFormListItem, type ManualEventInput,
} from "@/lib/helloasso-sync";

export const Route = createFileRoute("/transactions")({
  component: () => <Guard module="treasury"><ActivitiesCalendarPage /></Guard>,
});

const FORM_TYPE_LABELS: Record<string, string> = {
  Event: "Événement",
  Membership: "Adhésion",
  Donation: "Don",
  Shop: "Boutique",
  PaymentForm: "Paiement",
  CrowdFunding: "Financement",
  Checkout: "Caisse",
};

const FORM_TYPE_COLORS: Record<string, string> = {
  Event: "bg-primary/15 text-primary border-primary/30",
  Membership: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  Donation: "bg-pink-500/15 text-pink-700 dark:text-pink-400 border-pink-500/30",
  Shop: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  PaymentForm: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
  CrowdFunding: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
  Checkout: "bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/30",
};

function fmtMoney(v: number | string | null | undefined) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function ymd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ActivitiesCalendarPage() {
  const { hasAccess, loading: authLoading, user } = useAuth();
  const canWrite = hasAccess("treasury", "admin");
  const qc = useQueryClient();

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<HelloAssoFormListItem | null>(null);

  // Fenêtre élargie : on récupère tous les forms et on filtre côté client
  // pour pouvoir naviguer fluidement entre mois sans re-fetch.
  const { data: forms = [], isLoading } = useQuery({
    queryKey: ["helloasso-forms", user?.id],
    enabled: !!user && !authLoading,
    queryFn: () => fetchHelloAssoForms({}),
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forms;
    return forms.filter((f) =>
      (f.title ?? "").toLowerCase().includes(q) ||
      (f.form_slug ?? "").toLowerCase().includes(q),
    );
  }, [forms, search]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const monthLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  // Parse une date MySQL "YYYY-MM-DD HH:MM:SS" en Date locale (sans dérive UTC)
  const parseDbDate = (s: string | null | undefined): Date | null => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  };

  // Index : date "YYYY-MM-DD" → forms à afficher ce jour-là
  // - Event           → sur display_date uniquement
  // - Non-Event       → sur le 1er jour visible du mois où la plage [start..end]
  //                     (ou display_date à défaut) chevauche le mois affiché.
  //                     Permet de voir une adhésion "active jusqu'au 31/12" même
  //                     en mai, sans devoir naviguer jusqu'au mois de fin.
  const formsByDay = useMemo(() => {
    const map = new Map<string, HelloAssoFormListItem[]>();
    const mStart = monthStart;
    const mEnd = monthEnd;

    for (const f of filtered) {
      const isEvent = f.form_type === "Event";
      const display = parseDbDate(f.display_date);
      const start = parseDbDate(f.start_date) ?? display;
      const end = parseDbDate(f.end_date) ?? display;

      if (isEvent) {
        if (!display) continue;
        const key = ymd(display);
        (map.get(key) ?? map.set(key, []).get(key)!).push(f);
        continue;
      }

      // Non-Event : on couvre la période [start..end] (avec fallback display_date)
      const rangeStart = start ?? display;
      const rangeEnd = end ?? display;
      const isMembership = f.form_type === "Membership";

      // Pas de dates exploitables : on ancre quand même au 1er du mois affiché
      // (utile pour les campagnes d'adhésion permanentes sans date renseignée).
      if (!rangeStart && !rangeEnd) {
        const key = ymd(mStart);
        (map.get(key) ?? map.set(key, []).get(key)!).push(f);
        continue;
      }

      const periodStart = rangeStart ?? rangeEnd!;
      const periodEnd = rangeEnd ?? rangeStart!;

      // Pas de chevauchement avec le mois affiché.
      // Les campagnes d'adhésion (souvent longues / annuelles / passées)
      // restent visibles sur le calendrier : on les ancre au 1er du mois.
      if (periodEnd < mStart || periodStart > mEnd) {
        if (isMembership) {
          const key = ymd(mStart);
          (map.get(key) ?? map.set(key, []).get(key)!).push(f);
        }
        continue;
      }

      // Ancrage : le plus tard entre (début de période) et (1er du mois)
      const anchor = periodStart > mStart ? periodStart : mStart;
      const key = ymd(anchor);
      (map.get(key) ?? map.set(key, []).get(key)!).push(f);
    }
    return map;
  }, [filtered, monthStart, monthEnd]);

  const membershipForms = useMemo(
    () => forms.filter((f) => f.form_type === "Membership"),
    [forms],
  );

  // Construction grille calendrier (lundi = 1ʳᵉ colonne)
  const cells = useMemo(() => {
    const firstWeekday = (monthStart.getDay() + 6) % 7; // 0 = lundi
    const daysInMonth = monthEnd.getDate();
    const total = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    const arr: { date: Date | null; key: string }[] = [];
    for (let i = 0; i < total; i++) {
      const dayNum = i - firstWeekday + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        arr.push({ date: null, key: `pad-${i}` });
      } else {
        const d = new Date(cursor.getFullYear(), cursor.getMonth(), dayNum);
        arr.push({ date: d, key: ymd(d) });
      }
    }
    return arr;
  }, [cursor, monthStart, monthEnd]);

  async function onSync() {
    try {
      const r = await syncHelloAssoForms();
      toast.success(r.message ?? "Formulaires synchronisés");
      qc.invalidateQueries({ queryKey: ["helloasso-forms"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Erreur de synchronisation");
    }
  }

  return (
    <>
      <PageHeader
        title="Activités HelloAsso"
        description="Calendrier des formulaires HelloAsso (événements, adhésions, dons, boutiques…) et événements manuels"
        actions={canWrite ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => { setEditingEvent(null); setEventDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-1.5" /> Nouvel événement
            </Button>
            <SyncFormsButton onClick={onSync} />
          </div>
        ) : undefined}
      />

      <MembershipStatsBar
        forms={membershipForms}
        onSelect={(id) => setSelectedFormId(id)}
        selectedId={selectedFormId}
      />

      <div className="flex flex-col lg:flex-row gap-4">
        {/* ── Colonne calendrier ─────────────────────────── */}
        <Card className="flex-1 p-4 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Button size="icon" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-semibold capitalize min-w-[180px] text-center">{monthLabel}</h2>
              <Button size="icon" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCursor(startOfMonth(new Date()))}>
                Aujourd'hui
              </Button>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filtrer un formulaire…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="inline animate-spin h-4 w-4 mr-2" />Chargement des formulaires…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-px text-xs font-medium text-muted-foreground mb-1">
                {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
                  <div key={d} className="px-2 py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-px bg-border rounded-md overflow-hidden">
                {cells.map((cell) => {
                  const today = ymd(new Date());
                  const isToday = cell.date && ymd(cell.date) === today;
                  const dayForms = cell.date ? (formsByDay.get(ymd(cell.date)) ?? []) : [];
                  return (
                    <div
                      key={cell.key}
                      className={`bg-card min-h-[88px] p-1.5 text-xs ${cell.date ? "" : "bg-muted/40"}`}
                    >
                      {cell.date && (
                        <div className={`text-right text-[11px] font-medium mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                          {cell.date.getDate()}
                        </div>
                      )}
                      <div className="space-y-1">
                        {dayForms.slice(0, 3).map((f) => {
                          const color = FORM_TYPE_COLORS[f.form_type ?? ""] ?? "bg-muted text-foreground border-border";
                          const isSelected = selectedFormId === f.id;
                          return (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => setSelectedFormId(f.id)}
                              title={f.title ?? f.form_slug}
                              className={`w-full text-left truncate rounded px-1.5 py-0.5 border ${color} ${isSelected ? "ring-2 ring-primary" : ""} hover:opacity-80 transition`}
                            >
                              <span className="font-medium">{f.title ?? f.form_slug}</span>
                            </button>
                          );
                        })}
                        {dayForms.length > 3 && (
                          <div className="text-[10px] text-muted-foreground px-1">
                            +{dayForms.length - 3} autre{dayForms.length - 3 > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Legend />
            </>
          )}
        </Card>

        {/* ── Panneau de stats ────────────────────────────── */}
        <div className="w-full lg:w-[420px] flex-shrink-0">
          {selectedFormId ? (
            <FormDetailPanel
              formId={selectedFormId}
              onClose={() => setSelectedFormId(null)}
              canWrite={canWrite}
              onEdit={(f) => { setEditingEvent(f); setEventDialogOpen(true); }}
              onDeleted={() => { setSelectedFormId(null); qc.invalidateQueries({ queryKey: ["helloasso-forms"] }); }}
            />
          ) : (
            <Card className="p-6 text-center text-sm text-muted-foreground sticky top-4">
              <CalendarDays className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
              Cliquez sur un évènement du calendrier pour afficher ses statistiques de participation.
            </Card>
          )}
        </div>
      </div>

      <ManualEventDialog
        open={eventDialogOpen}
        onOpenChange={setEventDialogOpen}
        editing={editingEvent}
        onSaved={(id) => {
          qc.invalidateQueries({ queryKey: ["helloasso-forms"] });
          if (id) {
            qc.invalidateQueries({ queryKey: ["helloasso-form", id] });
            setSelectedFormId(id);
          }
        }}
      />
    </>
  );
}

function Legend() {
  const visibleTypes = Object.keys(FORM_TYPE_LABELS);
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {visibleTypes.map((t) => (
        <span key={t} className={`text-[11px] px-2 py-0.5 rounded border ${FORM_TYPE_COLORS[t]}`}>
          {FORM_TYPE_LABELS[t]}
        </span>
      ))}
    </div>
  );
}

function MembershipStatsBar({
  forms,
  onSelect,
  selectedId,
}: {
  forms: HelloAssoFormListItem[];
  onSelect: (id: string) => void;
  selectedId: string | null;
}) {
  const totals = useMemo(() => {
    let orders = 0, participants = 0, amount = 0;
    for (const f of forms) {
      orders += Number(f.orders_count ?? 0);
      participants += Number(f.participants_count ?? 0);
      amount += Number(f.total_amount ?? 0);
    }
    return { orders, participants, amount, campaigns: forms.length };
  }, [forms]);

  if (forms.length === 0) {
    return (
      <Card className="p-4 mb-4 border-emerald-500/30 bg-emerald-500/5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4 text-emerald-600" />
          Aucune campagne d'adhésion synchronisée. Cliquez sur « Synchroniser » pour les importer.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 mb-4">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={FORM_TYPE_COLORS.Membership}>Adhésions</Badge>
          <h3 className="font-semibold text-sm">Campagnes d'adhésion HelloAsso</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs">
          <KpiInline label="Campagnes" value={totals.campaigns.toString()} />
          <KpiInline label="Commandes" value={totals.orders.toString()} />
          <KpiInline label="Adhérents" value={totals.participants.toString()} />
          <KpiInline label="Collecté" value={fmtMoney(totals.amount)} accent />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {forms.map((f) => {
          const isSelected = selectedId === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => onSelect(f.id)}
              className={`text-left rounded-md border p-3 transition hover:bg-muted/40 ${
                isSelected ? "ring-2 ring-primary border-primary" : "border-border"
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="text-sm font-medium truncate" title={f.title ?? f.form_slug}>
                  {f.title ?? f.form_slug}
                </div>
                {f.state && (
                  <Badge variant="secondary" className="text-[10px] shrink-0">{f.state}</Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                <div>
                  <div className="text-muted-foreground">Cmd</div>
                  <div className="font-semibold tabular-nums">{Number(f.orders_count ?? 0)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Adh.</div>
                  <div className="font-semibold tabular-nums">{Number(f.participants_count ?? 0)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Total</div>
                  <div className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {fmtMoney(Number(f.total_amount ?? 0))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function KpiInline({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${accent ? "text-emerald-700 dark:text-emerald-400" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function SyncFormsButton({ onClick }: { onClick: () => void | Promise<void> }) {
  const [loading, setLoading] = useState(false);
  return (
    <Button
      size="sm"
      onClick={async () => {
        setLoading(true);
        try { await onClick(); } finally { setLoading(false); }
      }}
      disabled={loading}
    >
      {loading
        ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        : <RefreshCw className="h-4 w-4 mr-1.5" />}
      Synchroniser
    </Button>
  );
}

function FormDetailPanel({
  formId,
  onClose,
  canWrite,
  onEdit,
  onDeleted,
}: {
  formId: string;
  onClose: () => void;
  canWrite: boolean;
  onEdit: (f: HelloAssoFormListItem) => void;
  onDeleted: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["helloasso-form", formId],
    queryFn: () => fetchHelloAssoForm(formId),
    staleTime: 30_000,
  });

  if (isLoading || !data) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground sticky top-4">
        <Loader2 className="inline animate-spin h-4 w-4 mr-2" />Chargement…
      </Card>
    );
  }

  const { form, stats, tiers, participants, timeline } = data;
  const typeLabel = form.form_type ? (FORM_TYPE_LABELS[form.form_type] ?? form.form_type) : "—";
  const isManual = !!form.is_manual && Number(form.is_manual) === 1;

  async function onDelete() {
    if (!confirm("Supprimer définitivement cet événement manuel ?")) return;
    try {
      await deleteManualEvent(formId);
      toast.success("Événement supprimé");
      onDeleted();
    } catch (e: any) {
      toast.error(e?.message ?? "Suppression impossible");
    }
  }

  return (
    <Card className="p-4 sticky top-4 max-h-[calc(100vh-2rem)] flex flex-col">
      <div className="flex items-start gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className={FORM_TYPE_COLORS[form.form_type ?? ""] ?? ""}>{typeLabel}</Badge>
            {isManual && <Badge variant="secondary" className="text-[10px]">Manuel</Badge>}
          </div>
          <h3 className="font-semibold text-base mt-2 leading-tight">{form.title ?? form.form_slug}</h3>
          {!isManual && (
            <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{form.form_slug}</div>
          )}
        </div>
        {isManual && canWrite && (
          <>
            <Button size="icon" variant="ghost" onClick={() => onEdit(form as unknown as HelloAssoFormListItem)} aria-label="Modifier">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={onDelete} aria-label="Supprimer">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </>
        )}
        <Button size="icon" variant="ghost" onClick={onClose} aria-label="Fermer">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-1.5 text-xs text-muted-foreground mb-4">
        {form.start_date && (
          <div className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {new Date(form.start_date).toLocaleString("fr-FR")}
            {form.end_date && form.end_date !== form.start_date && (
              <> → {new Date(form.end_date).toLocaleString("fr-FR")}</>
            )}
          </div>
        )}
        {(form.place_name || form.place_city) && (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            {[form.place_name, form.place_city].filter(Boolean).join(" · ")}
          </div>
        )}
        {form.url && (
          <a href={form.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> Ouvrir sur HelloAsso
          </a>
        )}
        {form.state && <Badge variant="secondary" className="text-[10px]">{form.state}</Badge>}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat icon={<Users className="h-3.5 w-3.5" />} label="Participants" value={stats.participants_count.toString()} />
        <Stat icon={<Receipt className="h-3.5 w-3.5" />} label="Commandes" value={stats.orders_count.toString()} />
        <Stat icon={<Euro className="h-3.5 w-3.5" />} label="Collecté" value={fmtMoney(stats.total_amount)} />
      </div>

      <ScrollArea className="flex-1 -mr-2 pr-2">
        <Section title="Répartition par tarif">
          {tiers.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucune donnée d'item disponible.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Tarif</TableHead>
                  <TableHead className="text-xs text-right">Qté</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">
                      <div className="font-medium">{t.name ?? "—"}</div>
                      {t.type && <div className="text-[10px] text-muted-foreground">{t.type}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{t.quantity}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{fmtMoney(t.total_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>

        {stats.cash_out.length > 0 && (
          <Section title="Statut versements">
            <div className="space-y-1">
              {stats.cash_out.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{c.state}</span>
                  <span className="tabular-nums font-medium">{fmtMoney(c.amount)} <span className="text-muted-foreground">({c.n})</span></span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title={`Participants (${participants.length})`}>
          {participants.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun participant enregistré.</p>
          ) : (
            <div className="space-y-1.5">
              {participants.map((p, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-xs border-b pb-1.5 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">
                      {`${p.payer_first_name ?? ""} ${p.payer_last_name ?? ""}`.trim() || "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">{p.payer_email ?? "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium tabular-nums">{fmtMoney(p.total_amount)}</div>
                    <div className="text-[10px] text-muted-foreground">{p.orders_count} cmd.</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {timeline.length > 0 && (
          <Section title="Évolution des inscriptions">
            <Timeline data={timeline} />
          </Section>
        )}
      </ScrollArea>
    </Card>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border p-2 text-center">
      <div className="text-muted-foreground inline-flex items-center gap-1 text-[10px] uppercase tracking-wide">{icon}{label}</div>
      <div className="font-semibold text-sm mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</h4>
      {children}
    </div>
  );
}

function Timeline({ data }: { data: Array<{ day: string; orders: number | string; amount: number | string }> }) {
  const max = Math.max(...data.map((d) => Number(d.orders) || 0), 1);
  return (
    <div className="flex items-end gap-0.5 h-20">
      {data.map((d) => {
        const h = Math.max(2, (Number(d.orders) / max) * 80);
        return (
          <div
            key={d.day}
            className="flex-1 bg-primary/60 rounded-t hover:bg-primary transition"
            style={{ height: `${h}px` }}
            title={`${d.day} — ${d.orders} cmd. · ${fmtMoney(d.amount)}`}
          />
        );
      })}
    </div>
  );
}

function ManualEventDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: HelloAssoFormListItem | null;
  onSaved: (id: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(""); // YYYY-MM-DDTHH:mm
  const [description, setDescription] = useState("");
  const [placeName, setPlaceName] = useState("");
  const [placeCity, setPlaceCity] = useState("");
  const [placeAddress, setPlaceAddress] = useState("");
  const [saving, setSaving] = useState(false);

  // Convertit une date MySQL "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:mm" pour <input type="datetime-local">
  function toDtLocal(s: string | null | undefined): string {
    if (!s) return "";
    const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    return m ? `${m[1]}T${m[2]}` : "";
  }

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title ?? "");
      setDate(toDtLocal(editing.display_date));
      // Description/place_address ne sont pas dans le list payload — on les laisse vides à l'édition
      // (l'utilisateur les ressaisit s'il veut les modifier).
      setDescription("");
      setPlaceName(editing.place_name ?? "");
      setPlaceCity(editing.place_city ?? "");
      setPlaceAddress("");
    } else {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      setTitle("");
      setDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
      setDescription("");
      setPlaceName("");
      setPlaceCity("");
      setPlaceAddress("");
    }
  }, [open, editing]);

  async function handleSubmit() {
    if (!title.trim()) { toast.error("Le titre est requis"); return; }
    if (!date) { toast.error("La date est requise"); return; }
    setSaving(true);
    try {
      const payload: ManualEventInput = {
        title: title.trim(),
        display_date: date,
        description: description.trim() || null,
        place_name: placeName.trim() || null,
        place_city: placeCity.trim() || null,
        place_address: placeAddress.trim() || null,
      };
      if (editing) {
        const r = await updateManualEvent(editing.id, payload);
        toast.success("Événement modifié");
        onSaved(r?.data?.id ?? editing.id);
      } else {
        const r = await createManualEvent(payload);
        toast.success("Événement créé");
        // Toujours notifier (même si l'API ne renvoie pas d'id) pour rafraîchir le calendrier
        onSaved(r?.data?.id ?? "");
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto w-[calc(100%-1rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
          <DialogDescription>
            Événement manuel — ne sera jamais modifié par la synchronisation HelloAsso.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ev-title">Titre *</Label>
            <Input id="ev-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-date">Date *</Label>
            <Input id="ev-date" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-desc">Description</Label>
            <Textarea id="ev-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={5000} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ev-place">Lieu</Label>
              <Input id="ev-place" value={placeName} onChange={(e) => setPlaceName(e.target.value)} maxLength={255} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-city">Ville</Label>
              <Input id="ev-city" value={placeCity} onChange={(e) => setPlaceCity(e.target.value)} maxLength={255} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-address">Adresse</Label>
            <Input id="ev-address" value={placeAddress} onChange={(e) => setPlaceAddress(e.target.value)} maxLength={1000} />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="w-full sm:w-auto">
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-auto">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editing ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
